/**
 * The atlas's zoom / pan window (Story #6837 Stream 10a).
 *
 * Pure, and separate from the component, so the arithmetic the map claims can
 * be tested on its own: the window never leaves the drawing, zoom stays between
 * 1x and 6x, and the point under the pointer stays under the pointer while it
 * zooms. MyInventionsAtlas.tsx only turns pointer events into the numbers these
 * functions take; it does no zoom arithmetic of its own.
 *
 * A viewport is the rectangle of the drawing (900 x 560 user units, the same
 * VIEWPORT atlasGeometry lays the map out in) that the SVG's viewBox shows.
 * Its aspect ratio never changes, so `h` follows from `w`.
 */
import { VIEWPORT, type Point } from './atlasGeometry'

export interface Viewport {
  x: number
  y: number
  w: number
  h: number
}

export const MIN_ZOOM = 1
export const MAX_ZOOM = 6
/** One click of + or − (and one wheel notch) changes the zoom by this factor. */
export const ZOOM_STEP = 1.5

const { width: WORLD_W, height: WORLD_H } = VIEWPORT

/** The whole drawing: {0, 0, 900, 560}. */
export function fit(): Viewport {
  return { x: 0, y: 0, w: WORLD_W, h: WORLD_H }
}

/** The SVG viewBox attribute for a viewport; for fit() exactly "0 0 900 560". */
export function viewBoxString(vp: Viewport): string {
  return `${vp.x} ${vp.y} ${vp.w} ${vp.h}`
}

/** How far in the window is: 1 = the whole drawing, 6 = the tightest zoom. */
export function zoomLevel(vp: Viewport): number {
  return WORLD_W / vp.w
}

/** The zoom readout, one decimal: one + click from fit() reads "1.5×". */
export function formatZoom(vp: Viewport): string {
  return `${zoomLevel(vp).toFixed(1)}×`
}

/** Keep the window inside the drawing without changing its size. */
function clampPan(vp: Viewport): Viewport {
  return {
    ...vp,
    x: Math.min(Math.max(vp.x, 0), WORLD_W - vp.w),
    y: Math.min(Math.max(vp.y, 0), WORLD_H - vp.h),
  }
}

/**
 * Zoom by `factor` (> 1 in, < 1 out) about `focus`, a point in drawing
 * coordinates — the pointer for the wheel, the window centre for the buttons.
 * The zoom is clamped to 1x–6x, and the focus keeps its place in the window, so
 * the thing under the pointer is still under the pointer afterwards.
 */
export function zoomAt(vp: Viewport, factor: number, focus: Point): Viewport {
  const zoom = Math.min(Math.max(zoomLevel(vp) * factor, MIN_ZOOM), MAX_ZOOM)
  const w = WORLD_W / zoom
  const h = WORLD_H / zoom
  const [fx, fy] = focus
  return clampPan({
    x: fx - (fx - vp.x) * (w / vp.w),
    y: fy - (fy - vp.y) * (h / vp.h),
    w,
    h,
  })
}

/** Move the window by (dx, dy) drawing units, stopping at the drawing's edge. */
export function pan(vp: Viewport, dx: number, dy: number): Viewport {
  return clampPan({ ...vp, x: vp.x + dx, y: vp.y + dy })
}

/**
 * Where the window sits on a mini-map of the whole drawing, in the mini-map's
 * own pixels. At fit() it covers the whole mini-map.
 */
export function minimapRect(
  vp: Viewport,
  map: { width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  return {
    x: (vp.x / WORLD_W) * map.width,
    y: (vp.y / WORLD_H) * map.height,
    width: (vp.w / WORLD_W) * map.width,
    height: (vp.h / WORLD_H) * map.height,
  }
}

/**
 * Names, marks and strokes are drawn in drawing units, so zooming in would make
 * them grow with the map. Dividing a font size (or stroke width) by this keeps
 * it the size it is at fit(): 1 at fit(), 1.5 at 1.5x.
 */
export function labelScale(vp: Viewport): number {
  return WORLD_W / vp.w
}
